import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import type { Booking, Closure, Venue } from "../../shared/contracts";
import { useApp } from "../context";
import { date } from "../i18n";
import { dayMinute, rangesOverlap, tokyoDay } from "../../shared/venue-time";
export type CalendarData = {
  venue: Venue;
  bookings: (Booking & { title: string; brandName: string })[];
  closures: Closure[];
};
export function VenueCalendar({
  data,
  selectedDate,
  onSelectDate,
  agenda,
}: {
  data: CalendarData;
  selectedDate?: string;
  onSelectDate?: (day: string) => void;
  agenda?: ReactNode;
}) {
  const { t, language } = useApp();
  const [internalDate, setInternalDate] = useState(
    tokyoDay(new Date().toISOString()),
  );
  const selected = selectedDate ?? internalDate;
  const [month, setMonth] = useState(() => new Date(`${selected}T12:00:00`));
  useEffect(() => {
    if (selectedDate) setMonth(new Date(`${selectedDate}T12:00:00`));
  }, [selectedDate]);
  const onDay = (day: string) => {
    setInternalDate(day);
    onSelectDate?.(day);
  };
  const onDate = (record: { start: string; end: string }, day: string) =>
    rangesOverlap(record, {
      start: dayMinute(day, 0),
      end: dayMinute(day, 1440),
    });
  const year = month.getFullYear(),
    m = month.getMonth();
  const first = (new Date(year, m, 1).getDay() + 6) % 7,
    count = new Date(year, m + 1, 0).getDate();
  const days = Array.from(
    { length: Math.ceil((first + count) / 7) * 7 },
    (_, i) => i - first + 1,
  );
  const records = [
    ...data.bookings.map((b) => ({
      id: b.id,
      start: b.occupiedStart,
      end: b.occupiedEnd,
      title: b.title,
      eventId: b.eventId,
      closure: false,
    })),
    ...data.closures.map((c) => ({
      ...c,
      title: t("利用停止", "场地停用"),
      eventId: "",
      closure: true,
    })),
  ].sort((a, b) => a.start.localeCompare(b.start));
  const selectedRecords = records.filter((r) => onDate(r, selected));
  return (
    <div className="calendar-layout">
      <div className="calendar">
        <div className="calendar-heading">
          <button
            type="button"
            className="icon-button"
            aria-label={t("前の月", "上个月")}
            onClick={() => setMonth(new Date(year, m - 1, 1))}
          >
            <CaretLeft size={20} />
          </button>
          <h3>
            {year}
            {t("年", "年")} {m + 1}
            {t("月", "月")}
          </h3>
          <button
            type="button"
            className="icon-button"
            aria-label={t("次の月", "下个月")}
            onClick={() => setMonth(new Date(year, m + 1, 1))}
          >
            <CaretRight size={20} />
          </button>
        </div>
        <div className="calendar-grid">
          {(language === "ja"
            ? ["月", "火", "水", "木", "金", "土", "日"]
            : ["一", "二", "三", "四", "五", "六", "日"]
          ).map((d) => (
            <div className="weekday" key={d}>
              {d}
            </div>
          ))}
          {days.map((d, i) => {
            if (d < 1 || d > count)
              return <div key={i} className="calendar-blank" />;
            const key = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
              rows = records.filter((r) => onDate(r, key));
            return (
              <button
                type="button"
                key={i}
                className={
                  "calendar-day " + (selected === key ? "selected" : "")
                }
                onClick={() => onDay(key)}
                aria-pressed={selected === key}
                aria-label={`${key} ${rows.length} ${t("件の予定", "项占用")}`}
              >
                <span>{d}</span>
                {rows.slice(0, 2).map((r) => (
                  <span
                    className={"calendar-label " + (r.closure ? "closure" : "")}
                    key={r.id}
                  >
                    {r.title}
                  </span>
                ))}
                {rows.length > 2 && <small>+{rows.length - 2}</small>}
              </button>
            );
          })}
        </div>
        <div className="calendar-legend">
          <span className="legend-dot" />
          {t("確定した会場利用", "已确认场地利用")}
          <span className="legend-dot gray" />
          {t("利用停止", "停用")}
        </div>
      </div>
      <aside className="calendar-agenda">
        <h3>{selected}</h3>
        <p className="muted">{t("東京時間（JST）", "东京时间（JST）")}</p>
        {agenda ??
          (selectedRecords.length ? (
            selectedRecords.map((r) => (
              <div className="agenda-item" key={r.id}>
                <strong>{r.title}</strong>
                <p>
                  {date(r.start, language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  –
                  {date(r.end, language, {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
                {r.eventId && (
                  <Link className="text-link" to={"/events/" + r.eventId}>
                    {t("イベントを見る", "查看活动")}
                  </Link>
                )}
              </div>
            ))
          ) : (
            <p>
              {t(
                "確定予約・利用停止はありません。利用には運営の承認が必要です。",
                "没有已确认预约或停用记录。使用须经管理员批准。",
              )}
            </p>
          ))}
      </aside>
    </div>
  );
}
